/**
 * Staff magic-link sign-in entry.
 *
 * POSTs to `/internal/admin/auth/request` with `{ email }`. The route
 * always responds 200 (anti-enumeration), so the UI flips to the
 * "check your email" pane regardless of whether the email maps to a
 * staff row. Copy is fixed by ADR-0140:
 *
 *     "If that email is a staff account, a sign-in link is on its way."
 *
 * 429 surfaces a generic rate-limit copy; any other failure surfaces
 * the soft "could not reach the admin service" copy without flipping
 * the success state.
 *
 * On mount, reads the AuthProvider's state and, if the bootstrap
 * resolved into `service_error`, surfaces that as a banner above the
 * form so the staff member knows it wasn't just a 401.
 */

import { ArrowRight } from 'lucide-react';
import { useState } from 'react';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/NumLabel.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export function LoginView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (email.length === 0 || pending) return;
    setPending(true);
    setError(null);
    try {
      await api.requestSignIn(email);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'rate_limited') {
        setError('Too many sign-in attempts. Try again in a few minutes.');
      } else {
        setError('Could not reach the admin service. Try again in a moment.');
      }
    } finally {
      setPending(false);
    }
  };

  const serviceError = auth.state.kind === 'service_error';

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-20">
      <NumLabel n={0}>Admin sign in</NumLabel>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(40px, 7vw, 56px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -2,
          marginBottom: 24,
        }}
      >
        Staff console.
      </h1>

      {serviceError && !sent ? (
        <div
          className="mb-6 p-3"
          style={{
            background: C.amberDim,
            border: `1px solid ${C.amberFg}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
          }}
        >
          Could not verify your session — the admin service did not respond. Sign in
          again, or retry in a moment.
        </div>
      ) : null}

      {sent ? (
        <div
          className="p-6"
          style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
          }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.limeFg,
              letterSpacing: 2,
              marginBottom: 8,
              textTransform: 'uppercase',
            }}
          >
            Check your email
          </div>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 15,
              color: C.text,
              lineHeight: 1.6,
            }}
          >
            If that email is a staff account, a sign-in link is on its way.
          </p>
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setEmail('');
            }}
            className="mt-6"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              textDecoration: 'underline',
              background: 'transparent',
              border: 0,
              padding: 0,
            }}
          >
            Use a different address
          </button>
        </div>
      ) : (
        <>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 15,
              color: C.muted,
              lineHeight: 1.6,
              marginBottom: 24,
            }}
          >
            Magic link only. Staff access is gated by Cloudflare Access — you must
            already be on the staff allow-list.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="flex flex-col sm:flex-row items-stretch gap-2"
          >
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@sill.so"
              className="flex-1 px-4 py-3 outline-none"
              style={{
                fontFamily: monoFont,
                fontSize: 14,
                color: C.text,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
              aria-label="Staff email address"
            />
            <button
              type="submit"
              disabled={email.length === 0 || pending}
              className="flex items-center justify-center gap-2 px-6 py-3"
              style={{
                background: email.length > 0 ? C.lime : C.surface2,
                color: email.length > 0 ? C.ink : C.muted,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 2,
                border: 0,
                opacity: pending ? 0.6 : 1,
              }}
            >
              {pending ? 'Sending…' : 'Send link'} <ArrowRight size={15} />
            </button>
          </form>
          {error !== null ? (
            <div
              className="mt-3 p-3"
              style={{
                background: C.coralDim,
                border: `1px solid ${C.coralFg}`,
                borderRadius: 2,
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
              }}
            >
              {error}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
